import React, { useEffect, useState, useRef } from 'react';
import { ContactCard } from 'components/components/molecules';
import styled from 'styled-components';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { Section, Container } from 'components/shared';
import Mail from 'components/icons/Mail';
import CallIcon from 'components/icons/Call';
import { track } from 'utils/tracking';
import trackingEvents from 'utils/trackingEvents';

const StyledHeading = styled.h2`
  letter-spacing: -0.01em;
`;
const Grid = styled.div`
  place-items: center;
  ${({ isMobile }) =>
    isMobile
      ? 'padding-top: 1.2rem; padding-right: 4.5rem; '
      : 'padding-top: 1.5rem;'}
`;
const Card = styled.div`
  min-width: 18.75rem;
`;

const IconContainer = styled.div.attrs({
  className:
    'flex justify-center items-center w-30-mattamy h-30-mattamy bg-action-blue inline mr-2 rounded-full text-light-blue',
})`
  min-width: 1.875rem;
`;

const CallIconContainer = styled.div`
  min-width: 1.875rem;
`;
const ContactCorporatePanel = ({ fields }) => {
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );

  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const contactUsCards = getFieldValue(fields, 'contactUsCards') || [];

  const filteredCards = contactUsCards
    .filter((c) => c.heading)
    .sort((a, b) =>
      a.heading.toLowerCase() > b.heading.toLowerCase() ? 1 : -1,
    );

  const [ClassWidth, setClassWidth] = useState('md:w-7/12');
  const baseFontSizeRef = useRef();
  useEffect(() => {
    const handleResize = () => {
      const currentZoomLevel = window.devicePixelRatio;
      if (currentZoomLevel >= 2) {
        setClassWidth('md:w-10/12');
      } else {
        setClassWidth('md:w-7/12');
      }
    };
    const observer = new ResizeObserver(checkFontSizeChange);
    observer.observe(baseFontSizeRef.current);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);
  const checkFontSizeChange = () => {
    if (
      typeof window !== 'undefined' &&
      baseFontSizeRef &&
      baseFontSizeRef.current
    ) {
      const fontSize = parseInt(
        getComputedStyle(baseFontSizeRef.current).fontSize,
        10,
      );
      if (fontSize >= 18) {
        setClassWidth('md:w-12/12');
      } else {
        setClassWidth('md:w-7/12');
      }
    } else {
      setClassWidth('md:w-7/12');
    }
  };

  return (
    <Section className='bg-light-blue mt-4 md:mt-8'>
      <Container className='flex flex-col mt-8 items-left md:items-center'>
        <StyledHeading className='text-3xl md:text-4xl text-mattamy-blue font-trade-gothic-20  mb-3 md:mb-2'>
          {'Contact Our Corporate Offices'}
        </StyledHeading>
        <Grid
          className={`w-full justify-center ${ClassWidth} md:grid md:grid-cols-2 md:gap-x-12 mt-1 md:mt-0 select-none`}
          isMobile={isMobile}
        >
          {filteredCards.map((contactUsCard, index) => (
            <div className='self-left items-left justify-left' key={index}>
              <Card className='rounded mb-16 md:mb-20' isMobile={isMobile}>
                <h3 className='text-2xl md:text-3xl text-mattamy-blue font-trade-gothic-20 leading-9 md:mb-1 tracking-tight'>
                  {contactUsCard.heading}
                </h3>
                <div className='flex flex-col h-full w-full font-graphie text-base text-action-blue'>
                  {contactUsCard.emailAddress && (
                    <div className=' flex items-center'>
                      <IconContainer aria-hidden='true'>
                        <Mail className='h-4 w-4' />
                      </IconContainer>
                      <a
                        ref={baseFontSizeRef}
                        className='break-all'
                        href={`mailto:${contactUsCard.emailAddress}`}
                        onClick={() => {
                          track({ eventId: trackingEvents.Email });
                        }}
                        aria-label={'Email to ' + contactUsCard.emailAddress}
                      >
                        {contactUsCard.emailAddress}
                      </a>
                    </div>
                  )}
                  {contactUsCard.phone && (
                    <div className='mt-2 flex items-center'>
                      <CallIconContainer aria-hidden='true'>
                        <CallIcon className='w-30-mattamy h-30-mattamy mr-2' />
                      </CallIconContainer>
                      <a
                        className='break-all'
                        href={`tel:${contactUsCard.phone}`}
                        onClick={() => {
                          track({ eventId: trackingEvents.ClickToCall });
                        }}
                        aria-label={'Call to ' + contactUsCard.phone}
                      >
                        {contactUsCard.phone}
                      </a>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default ContactCorporatePanel;
